import { useNavigate, useRouter } from '@tanstack/react-router';
import {
	CalendarSync,
	LogIn,
	LogOut,
	ShoppingCart,
	UserRoundPlus,
} from 'lucide-react';

import config from '@/config';
import { USER_ROLE } from '@/constants';
import { AuthCognito } from '@/config.type';

import {
	AUTH_KEY,
	IS_SUBSCRIBED_KEY,
} from '@/views/home/auth/Authentication.hooks';

export class Button {
	static SignOut = SignOutButton;
	static SignUp = SignUpButton;
	static SignIn = SignInButton;
	static Subscribe = SubscribeButton;
	static ManageSubscription = ManageSubscriptionButton;
}

function SignOutButton() {
	const router = useRouter();
	const basename = `${router.basepath}/logout`;
	const isAuthenticated = localStorage.getItem(AUTH_KEY);

	if (!isAuthenticated) {
		return null;
	}

	if (!config.app.auth.enabled) {
		return null;
	}

	const redirectUri = [location.protocol, '//', location.host, basename].join(
		'',
	);

	const auth = config.app.auth as AuthCognito;
	const redirectSections = [
		`client_id=${auth.config.clientId}`,
		`logout_uri=${redirectUri}`,
	].join('&');

	const redirectUrl = [auth.config.host, '/logout?', redirectSections].join(
		'',
	);

	return (
		<button
			type={'button'}
			onClick={() => {
				location.href = redirectUrl;
			}}
		>
			<span>Sign Out</span>
			<span>
				<LogOut />
			</span>
		</button>
	);
}

function SignUpButton() {
	const router = useRouter();
	const basename = `${router.basepath}/callback`;
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';

	if (isAdmin) {
		return null;
	}
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}

	if (!config.app.auth.enabled) {
		return null;
	}

	if (isAuthenticated) {
		return null;
	}

	const redirectUri = [location.protocol, '//', location.host, basename].join(
		'',
	);

	const auth = config.app.auth as AuthCognito;
	const redirectSections = [
		`client_id=${auth.config.clientId}`,
		'response_type=code',
		'scope=email+openid',
		`redirect_uri=${redirectUri}`,
		`state=${btoa(window.location.pathname)}`,
	].join('&');

	const redirectUrl = [auth.config.host, '/signup?', redirectSections].join(
		'',
	);

	return (
		<button
			type={'button'}
			onClick={() => {
				location.href = redirectUrl;
			}}
		>
			<span>Sign Up</span>
			<span>
				<UserRoundPlus />
			</span>
		</button>
	);
}

function SignInButton() {
	const router = useRouter();
	const basename = `${router.basepath}/callback`;
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';

	if (isAdmin) {
		return null;
	}
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}

	if (!config.app.auth.enabled) {
		return null;
	}

	if (isAuthenticated) {
		return null;
	}

	const redirectUri = [location.protocol, '//', location.host, basename].join(
		'',
	);

	const auth = config.app.auth as AuthCognito;
	const redirectSections = [
		`client_id=${auth.config.clientId}`,
		'response_type=code',
		'scope=email+openid',
		`redirect_uri=${redirectUri}`,
		`state=${btoa(window.location.pathname)}`,
	].join('&');

	const redirectUrl = [auth.config.host, '/login?', redirectSections].join(
		'',
	);

	return (
		<button
			type={'button'}
			onClick={() => {
				location.href = redirectUrl;
			}}
		>
			<span>Sign In</span>
			<span>
				<LogIn />
			</span>
		</button>
	);
}

function SubscribeButton() {
	const navigate = useNavigate();
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	const isSubscribed = localStorage.getItem(IS_SUBSCRIBED_KEY);
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	if (isAdmin) {
		return null;
	}
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}

	if (isSubscribed || !isAuthenticated) {
		return null;
	}

	return (
		<button
			type={'button'}
			onClick={() => {
				navigate({
					to: '/subscriptions',
				});
			}}
		>
			<span>Subscribe</span>
			<span>
				<ShoppingCart />
			</span>
		</button>
	);
}

function ManageSubscriptionButton() {
	const isAuthenticated = localStorage.getItem(AUTH_KEY);
	const isSubscribed = localStorage.getItem(IS_SUBSCRIBED_KEY);
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	if (isAdmin) {
		return null;
	}
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}

	const auth = config.app.auth as AuthCognito;

	if (!isSubscribed || !isAuthenticated) {
		return null;
	}

	const { customerPortalUrl } = auth.config;
	if (!customerPortalUrl) {
		return null;
	}

	return (
		<button
			type={'button'}
			onClick={() => {
				const r = open(customerPortalUrl, '_blank');
				if (r !== null) {
					r.focus();
				}
			}}
		>
			<span>Manage Subscription</span>
			<span>
				<CalendarSync />
			</span>
		</button>
	);
}
